<script setup lang="ts">
import { computed, nextTick, ref, shallowRef, watch } from 'vue'
import { PhPlay } from '@phosphor-icons/vue'
import { storeToRefs } from 'pinia'
import { useElementVisibility } from '@/composables/useElementVisibility'
import { useElementWidth } from '@/composables/useElementWidth'
import { useMediaLifecycle } from '@/composables/useMediaLifecycle'
import { useMediaSoundPreference } from '@/composables/useMediaSoundPreference'
import { DISTRIBUTE_PREVIEW_HINT } from '@/constants/distribute'
import { useFeedPreferencesStore } from '@/stores/feedPreferences'
import {
  buildDisplayMediaImageUrl,
  normalizeMediaUrl,
  resolveMediaRequestWidth,
  type MediaSourceType,
} from '@/utils/distribute/media-url'
import { getRuntimePerformanceBudget } from '@/utils/performance/runtime-budget'
import DreamaImage from '@/components/distribute-dreama-image'
import MediaSoundToggle from '@/components/distribute-media-sound-toggle'
import type { Props } from './type'

defineOptions({
  name: 'DistributeHoverVideo',
})

const props = withDefaults(defineProps<Props>(), {
  src: null,
  poster: null,
  aspectRatio: '4 / 5',
  label: '视频',
  mediaWidth: null,
  priority: false,
  autoplayActive: false,
})
const emit = defineEmits<{
  'preview-ready': []
}>()
const runtimeBudget = getRuntimePerformanceBudget()
const feedPreferencesStore = useFeedPreferencesStore()
const { isDiscoverAutoplayEnabled } = storeToRefs(feedPreferencesStore)

const rootElement = ref<HTMLDivElement | null>(null)
const mediaElement = ref<HTMLVideoElement | null>(null)
const isVideoReady = shallowRef(false)
const hasEmittedPreviewReady = shallowRef(false)
const isHovering = shallowRef(false)
const { isPlaybackMuted, toggleSound } = useMediaSoundPreference()
const canAutoplayPreview = computed(() => isDiscoverAutoplayEnabled.value && props.autoplayActive)
const { width: measuredWidth } = useElementWidth(rootElement)
const { isVisible: shouldLoadMedia } = useElementVisibility(rootElement, {
  initialVisible: false,
  rootMargin: runtimeBudget.mediaPreloadRootMargin,
  threshold: 0,
})
const { isPlaying, play, stop } = useMediaLifecycle(mediaElement, {
  autoPlayWhenVisible: canAutoplayPreview,
  muted: isPlaybackMuted,
})

const previewVideoSrc = computed(() => normalizeMediaUrl(props.src))
const posterUrl = computed(() => normalizeMediaUrl(props.poster))
const canPreview = computed(() => Boolean(previewVideoSrc.value))
const posterSource = computed(() => posterUrl.value ?? previewVideoSrc.value)
const posterSourceType = computed<MediaSourceType>(() =>
  posterUrl.value ? 'image' : 'video-frame',
)
const layoutWidth = computed(() =>
  typeof props.mediaWidth === 'number' && props.mediaWidth > 0
    ? props.mediaWidth
    : measuredWidth.value,
)
const posterRequestWidth = computed(() =>
  resolveMediaRequestWidth(layoutWidth.value, {
    dpr: typeof window === 'undefined' ? 1 : window.devicePixelRatio,
  }),
)
const videoPosterSrc = computed(() =>
  buildDisplayMediaImageUrl(posterSource.value, {
    height: 0,
    sourceType: posterSourceType.value,
    width: posterRequestWidth.value,
  }),
)
const shouldRenderVideo = computed(() => {
  if (!canPreview.value) {
    return false
  }

  if (canAutoplayPreview.value) {
    return shouldLoadMedia.value || isHovering.value
  }

  return isHovering.value
})
const shouldShowVideo = computed(() => isPlaying.value && isVideoReady.value)
const isVideoLoading = computed(
  () => canPreview.value && shouldRenderVideo.value && !isVideoReady.value,
)

watch(
  () => [previewVideoSrc.value, posterSource.value],
  () => {
    isVideoReady.value = false
    hasEmittedPreviewReady.value = false
  },
)

watch(shouldRenderVideo, (renderVideo) => {
  if (!renderVideo) {
    isVideoReady.value = false
  }
})

watch(
  shouldRenderVideo,
  async (renderVideo) => {
    if (!renderVideo || !isHovering.value) {
      return
    }

    await nextTick()
    void play()
  },
  { flush: 'post' },
)

function markVideoReady() {
  isVideoReady.value = true
  emitPreviewReady()
}

function handleToggleSound() {
  toggleSound()
  void play()
}

function handleMouseEnter() {
  isHovering.value = true
  void play()
}

function handleMouseLeave() {
  isHovering.value = false
  stop()
}

function handlePosterReady() {
  emitPreviewReady()
}

function emitPreviewReady() {
  if (hasEmittedPreviewReady.value) {
    return
  }

  hasEmittedPreviewReady.value = true
  emit('preview-ready')
}
</script>

<template>
  <div
    ref="rootElement"
    class="hover-video"
    :style="{ '--preview-aspect': aspectRatio }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <DreamaImage
      class="hover-video__poster"
      :src="posterSource"
      :source-type="posterSourceType"
      :aspect-ratio="aspectRatio"
      :priority="priority"
      :request-width="mediaWidth"
      @preview-ready="handlePosterReady"
    />

    <video
      v-if="shouldRenderVideo"
      ref="mediaElement"
      class="hover-video__media"
      :class="{ 'is-visible': shouldShowVideo }"
      :src="previewVideoSrc ?? undefined"
      :poster="videoPosterSrc ?? undefined"
      loop
      playsinline
      preload="metadata"
      @loadeddata="markVideoReady"
      @canplay="markVideoReady"
      @playing="markVideoReady"
    />

    <MediaSoundToggle
      v-if="canPreview"
      :muted="isPlaybackMuted"
      :loading="isVideoLoading"
      @toggle="handleToggleSound"
    />

    <div class="hover-video__shade" />

    <slot :is-playing="isPlaying">
      <div class="hover-video__fallback">
        <span class="hover-video__label">{{ label }}</span>
        <span class="hover-video__trigger">
          <PhPlay :size="16" weight="fill" />
          {{ DISTRIBUTE_PREVIEW_HINT }}
        </span>
      </div>
    </slot>
  </div>
</template>

<style scoped>
.hover-video {
  position: relative;
  aspect-ratio: var(--preview-aspect);
  overflow: hidden;
  border-radius: 12px;
  background: var(--app-skeleton-base);
}

.hover-video__poster,
.hover-video__media,
.hover-video__shade,
.hover-video__fallback {
  position: absolute;
  inset: 0;
}

.hover-video__media {
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.24s ease;
}

.hover-video__media.is-visible {
  opacity: 1;
}

.hover-video__poster {
  z-index: 0;
}

.hover-video__shade {
  display: none;
}

.hover-video:hover :deep(.media-sound-toggle) {
  opacity: 1;
  pointer-events: auto;
}

.hover-video:hover :deep(.cover-meta__bgm),
.hover-video:focus-within :deep(.cover-meta__bgm) {
  animation-play-state: running;
}

.hover-video__fallback {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 18px;
}

.hover-video__label,
.hover-video__trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  backdrop-filter: blur(14px);
}

.hover-video__label {
  background: rgba(255, 255, 255, 0.18);
  color: white;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hover-video__trigger {
  align-self: flex-start;
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.92);
  font-size: 13px;
  font-weight: 600;
}
</style>
